import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import ClientNav from '@/components/ClientNav';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Globe, Save, RefreshCw, Plus, Trash2, Home, Briefcase, DollarSign, FolderOpen, Mail, Info, Menu, FileCode } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminWebsiteEditor = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('homepage');
  
  // All content sections
  const [content, setContent] = useState({
    homepage: {
      hero_title: '',
      hero_subtitle: '',
      hero_cta_primary: '',
      hero_cta_secondary: '',
      features_title: '',
      features: []
    },
    services: {
      title: '',
      subtitle: '',
      services_list: []
    },
    pricing: {
      title: '',
      subtitle: '',
      plans: []
    },
    portfolio: {
      title: '',
      subtitle: '',
      projects: []
    },
    contact: {
      title: '',
      subtitle: '',
      email: '',
      phone: '',
      location: '',
      address_line1: '',
      address_line2: ''
    },
    about: {
      company_name: '',
      tagline: '',
      description: '',
      team: []
    },
    footer: {
      copyright: '',
      tagline: '',
      social_links: {}
    },
    navigation: {
      logo_text: '',
      menu_items: [],
      cta_text: ''
    }
  });

  useEffect(() => {
    fetchAllContent();
  }, []);

  const fetchAllContent = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/content/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newContent = { ...content };
      response.data.content.forEach(item => {
        if (item.section && item.content) {
          newContent[item.section] = { ...newContent[item.section], ...item.content };
        }
      });
      setContent(newContent);
    } catch (error) {
      console.error('Failed to fetch content:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const saveSection = async (section) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content/${section}`, content[section], {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      toast.success(`${section.charAt(0).toUpperCase() + section.slice(1)} saved!`);
    } catch (error) {
      console.error('Failed to save content:', error);
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (section, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const addArrayItem = (section, field, defaultItem) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: [...(prev[section][field] || []), defaultItem]
      }
    }));
  };

  const updateArrayItem = (section, field, index, value) => {
    setContent(prev => {
      const newArray = [...(prev[section][field] || [])];
      newArray[index] = value;
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: newArray
        }
      };
    });
  };

  const removeArrayItem = (section, field, index) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: prev[section][field].filter((_, i) => i !== index)
      }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712]">
        <ClientNav />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  const tabConfig = [
    { id: 'homepage', label: 'Home', icon: Home },
    { id: 'services', label: 'Services', icon: Briefcase },
    { id: 'pricing', label: 'Pricing', icon: DollarSign },
    { id: 'portfolio', label: 'Portfolio', icon: FolderOpen },
    { id: 'contact', label: 'Contact', icon: Mail },
    { id: 'about', label: 'About', icon: Info },
    { id: 'navigation', label: 'Nav/Footer', icon: Menu },
  ];

  return (
    <div className="min-h-screen bg-[#030712]" data-testid="admin-website-editor">
      <ClientNav />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold font-['Syne'] text-white mb-2" data-testid="editor-title">
              Website Content Editor
            </h1>
            <p className="text-slate-400">Edit all pages and content - changes reflect immediately</p>
          </div>
          <Button
            onClick={fetchAllContent}
            variant="outline"
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
            data-testid="refresh-content-btn"
          >
            <RefreshCw size={18} className="mr-2" />
            Refresh
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-slate-900 border border-slate-800 p-1 mb-6 flex flex-wrap gap-1">
            {tabConfig.map(tab => (
              <TabsTrigger 
                key={tab.id} 
                value={tab.id}
                className="flex items-center gap-2"
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon size={16} />
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* HOMEPAGE EDITOR */}
          <TabsContent value="homepage" data-testid="homepage-editor">
            <div className="bg-slate-950 border border-slate-800 rounded-xl p-8">
              <h2 className="text-2xl font-bold font-['Syne'] text-white mb-6 flex items-center">
                <Home size={24} className="mr-2 text-indigo-400" />
                Homepage Content
              </h2>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Hero Title</label>
                    <Input
                      value={content.homepage.hero_title}
                      onChange={(e) => updateField('homepage', 'hero_title', e.target.value)}
                      placeholder="Professional Web & Network Solutions"
                      className="bg-slate-900 border-slate-800 text-white"
                      data-testid="hero-title-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Features Section Title</label>
                    <Input
                      value={content.homepage.features_title}
                      onChange={(e) => updateField('homepage', 'features_title', e.target.value)}
                      placeholder="Why Choose Us"
                      className="bg-slate-900 border-slate-800 text-white"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Hero Subtitle</label>
                  <Textarea
                    value={content.homepage.hero_subtitle}
                    onChange={(e) => updateField('homepage', 'hero_subtitle', e.target.value)}
                    placeholder="Expert website design and home networking services..."
                    rows={3}
                    className="bg-slate-900 border-slate-800 text-white resize-none"
                    data-testid="hero-subtitle-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Primary CTA Text</label>
                    <Input
                      value={content.homepage.hero_cta_primary}
                      onChange={(e) => updateField('homepage', 'hero_cta_primary', e.target.value)}
                      placeholder="View Plans"
                      className="bg-slate-900 border-slate-800 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Secondary CTA Text</label>
                    <Input
                      value={content.homepage.hero_cta_secondary}
                      onChange={(e) => updateField('homepage', 'hero_cta_secondary', e.target.value)}
                      placeholder="Get In Touch"
                      className="bg-slate-900 border-slate-800 text-white"
                    />
                  </div>
                </div>

                {/* Features */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-slate-300">Homepage Features</label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addArrayItem('homepage', 'features', { title: '', description: '', icon: '' })}
                      className="border-slate-700 text-slate-300"
                    >
                      <Plus size={16} className="mr-1" /> Add Feature
                    </Button>
                  </div>
                  {(content.homepage.features || []).map((feature, idx) => (
                    <div key={idx} className="bg-slate-900 p-4 rounded-lg mb-3 border border-slate-800">
                      <div className="flex justify-between mb-2">
                        <span className="text-xs text-slate-500">Feature {idx + 1}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeArrayItem('homepage', 'features', idx)}
                          className="text-red-400 hover:text-red-300 h-6 px-2"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <Input
                          value={feature.title}
                          onChange={(e) => updateArrayItem('homepage', 'features', idx, { ...feature, title: e.target.value })}
                          placeholder="Feature title"
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                        <Input
                          value={feature.icon}
                          onChange={(e) => updateArrayItem('homepage', 'features', idx, { ...feature, icon: e.target.value })}
                          placeholder="Icon name"
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                        <Input
                          value={feature.description}
                          onChange={(e) => updateArrayItem('homepage', 'features', idx, { ...feature, description: e.target.value })}
                          placeholder="Description"
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                    </div>
                  ))}
                </div>
                
                <Button
                  onClick={() => saveSection('homepage')}
                  disabled={saving}
                  className="bg-indigo-500 hover:bg-indigo-600 text-white w-full"
                  data-testid="save-homepage"
                >
                  <Save size={18} className="mr-2" />
                  {saving ? 'Saving...' : 'Save Homepage'}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* SERVICES EDITOR */}
          <TabsContent value="services" data-testid="services-editor">
            <div className="bg-slate-950 border border-slate-800 rounded-xl p-8">
              <h2 className="text-2xl font-bold font-['Syne'] text-white mb-6 flex items-center">
                <Briefcase size={24} className="mr-2 text-indigo-400" />
                Services Page
              </h2>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Page Title</label>
                    <Input
                      value={content.services.title}
                      onChange={(e) => updateField('services', 'title', e.target.value)}
                      placeholder="Our Services"
                      className="bg-slate-900 border-slate-800 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Subtitle</label>
                    <Input
                      value={content.services.subtitle}
                      onChange={(e) => updateField('services', 'subtitle', e.target.value)}
                      placeholder="Comprehensive solutions..."
                      className="bg-slate-900 border-slate-800 text-white"
                    />
                  </div>
                </div>

                {/* Services List */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-slate-300">Services</label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addArrayItem('services', 'services_list', { name: '', description: '', price: '', features: [] })}
                      className="border-slate-700 text-slate-300"
                    >
                      <Plus size={16} className="mr-1" /> Add Service
                    </Button>
                  </div>
                  {(content.services.services_list || []).map((service, idx) => (
                    <div key={idx} className="bg-slate-900 p-4 rounded-lg mb-3 border border-slate-800">
                      <div className="flex justify-between mb-2">
                        <span className="text-xs text-slate-500">Service {idx + 1}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeArrayItem('services', 'services_list', idx)}
                          className="text-red-400 hover:text-red-300 h-6 px-2"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <Input
                          value={service.name}
                          onChange={(e) => updateArrayItem('services', 'services_list', idx, { ...service, name: e.target.value })}
                          placeholder="Service name"
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                        <Input
                          value={service.price}
                          onChange={(e) => updateArrayItem('services', 'services_list', idx, { ...service, price: e.target.value })}
                          placeholder="Starting from £..."
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                      <Textarea
                        value={service.description}
                        onChange={(e) => updateArrayItem('services', 'services_list', idx, { ...service, description: e.target.value })}
                        placeholder="Service description"
                        rows={2}
                        className="bg-slate-800 border-slate-700 text-white resize-none"
                      />
                    </div>
                  ))}
                </div>
                
                <Button
                  onClick={() => saveSection('services')}
                  disabled={saving}
                  className="bg-indigo-500 hover:bg-indigo-600 text-white w-full"
                >
                  <Save size={18} className="mr-2" />
                  {saving ? 'Saving...' : 'Save Services'}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* PRICING EDITOR */}
          <TabsContent value="pricing" data-testid="pricing-editor">
            <div className="bg-slate-950 border border-slate-800 rounded-xl p-8">
              <h2 className="text-2xl font-bold font-['Syne'] text-white mb-6 flex items-center">
                <DollarSign size={24} className="mr-2 text-indigo-400" />
                Pricing Page
              </h2>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Page Title</label>
                    <Input
                      value={content.pricing.title}
                      onChange={(e) => updateField('pricing', 'title', e.target.value)}
                      placeholder="Simple, Transparent Pricing"
                      className="bg-slate-900 border-slate-800 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Subtitle</label>
                    <Input
                      value={content.pricing.subtitle}
                      onChange={(e) => updateField('pricing', 'subtitle', e.target.value)}
                      placeholder="Choose the plan that's right for you"
                      className="bg-slate-900 border-slate-800 text-white"
                    />
                  </div>
                </div>

                <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
                  <p className="text-sm text-slate-400">
                    <strong>Note:</strong> Pricing plans (Starter, Professional, Business) are configured in the backend. 
                    Use this section to customize the page text and add any custom pricing tiers for display only.
                  </p>
                </div>
                
                <Button
                  onClick={() => saveSection('pricing')}
                  disabled={saving}
                  className="bg-indigo-500 hover:bg-indigo-600 text-white w-full"
                >
                  <Save size={18} className="mr-2" />
                  {saving ? 'Saving...' : 'Save Pricing'}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* PORTFOLIO EDITOR */}
          <TabsContent value="portfolio" data-testid="portfolio-editor">
            <div className="bg-slate-950 border border-slate-800 rounded-xl p-8">
              <h2 className="text-2xl font-bold font-['Syne'] text-white mb-6 flex items-center">
                <FolderOpen size={24} className="mr-2 text-indigo-400" />
                Portfolio Page
              </h2>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Page Title</label>
                    <Input
                      value={content.portfolio.title}
                      onChange={(e) => updateField('portfolio', 'title', e.target.value)}
                      placeholder="Our Work"
                      className="bg-slate-900 border-slate-800 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Subtitle</label>
                    <Input
                      value={content.portfolio.subtitle}
                      onChange={(e) => updateField('portfolio', 'subtitle', e.target.value)}
                      placeholder="Recent projects we're proud of"
                      className="bg-slate-900 border-slate-800 text-white"
                    />
                  </div>
                </div>

                {/* Projects */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-slate-300">Portfolio Projects</label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addArrayItem('portfolio', 'projects', { title: '', description: '', image_url: '', link: '', tags: [] })}
                      className="border-slate-700 text-slate-300"
                    >
                      <Plus size={16} className="mr-1" /> Add Project
                    </Button>
                  </div>
                  {(content.portfolio.projects || []).map((project, idx) => (
                    <div key={idx} className="bg-slate-900 p-4 rounded-lg mb-3 border border-slate-800">
                      <div className="flex justify-between mb-2">
                        <span className="text-xs text-slate-500">Project {idx + 1}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeArrayItem('portfolio', 'projects', idx)}
                          className="text-red-400 hover:text-red-300 h-6 px-2"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <Input
                          value={project.title}
                          onChange={(e) => updateArrayItem('portfolio', 'projects', idx, { ...project, title: e.target.value })}
                          placeholder="Project title"
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                        <Input
                          value={project.link}
                          onChange={(e) => updateArrayItem('portfolio', 'projects', idx, { ...project, link: e.target.value })}
                          placeholder="https://..."
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                      <Input
                        value={project.image_url}
                        onChange={(e) => updateArrayItem('portfolio', 'projects', idx, { ...project, image_url: e.target.value })}
                        placeholder="Image URL"
                        className="bg-slate-800 border-slate-700 text-white mb-3"
                      />
                      <Textarea
                        value={project.description}
                        onChange={(e) => updateArrayItem('portfolio', 'projects', idx, { ...project, description: e.target.value })}
                        placeholder="Project description"
                        rows={2}
                        className="bg-slate-800 border-slate-700 text-white resize-none"
                      />
                    </div>
                  ))}
                </div>
                
                <Button
                  onClick={() => saveSection('portfolio')}
                  disabled={saving}
                  className="bg-indigo-500 hover:bg-indigo-600 text-white w-full"
                >
                  <Save size={18} className="mr-2" />
                  {saving ? 'Saving...' : 'Save Portfolio'}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* CONTACT EDITOR */}
          <TabsContent value="contact" data-testid="contact-editor">
            <div className="bg-slate-950 border border-slate-800 rounded-xl p-8">
              <h2 className="text-2xl font-bold font-['Syne'] text-white mb-6 flex items-center">
                <Mail size={24} className="mr-2 text-indigo-400" />
                Contact Page
              </h2>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Page Title</label>
                    <Input
                      value={content.contact.title}
                      onChange={(e) => updateField('contact', 'title', e.target.value)}
                      placeholder="Get In Touch"
                      className="bg-slate-900 border-slate-800 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Subtitle</label>
                    <Input
                      value={content.contact.subtitle}
                      onChange={(e) => updateField('contact', 'subtitle', e.target.value)}
                      placeholder="Ready to start your project?"
                      className="bg-slate-900 border-slate-800 text-white"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Email Address</label>
                    <Input
                      value={content.contact.email}
                      onChange={(e) => updateField('contact', 'email', e.target.value)}
                      placeholder="clients@dhhomenet.co.uk"
                      className="bg-slate-900 border-slate-800 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Phone Number</label>
                    <Input
                      value={content.contact.phone}
                      onChange={(e) => updateField('contact', 'phone', e.target.value)}
                      placeholder="+44 (0) 1443 123456"
                      className="bg-slate-900 border-slate-800 text-white"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Location</label>
                  <Input
                    value={content.contact.location}
                    onChange={(e) => updateField('contact', 'location', e.target.value)}
                    placeholder="Pontypridd, Wales"
                    className="bg-slate-900 border-slate-800 text-white"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Address Line 1</label>
                    <Input
                      value={content.contact.address_line1}
                      onChange={(e) => updateField('contact', 'address_line1', e.target.value)}
                      placeholder="Street address"
                      className="bg-slate-900 border-slate-800 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Address Line 2</label>
                    <Input
                      value={content.contact.address_line2}
                      onChange={(e) => updateField('contact', 'address_line2', e.target.value)}
                      placeholder="City, postal code"
                      className="bg-slate-900 border-slate-800 text-white"
                    />
                  </div>
                </div>
                
                <Button
                  onClick={() => saveSection('contact')}
                  disabled={saving}
                  className="bg-indigo-500 hover:bg-indigo-600 text-white w-full"
                >
                  <Save size={18} className="mr-2" />
                  {saving ? 'Saving...' : 'Save Contact'}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* ABOUT EDITOR */}
          <TabsContent value="about" data-testid="about-editor">
            <div className="bg-slate-950 border border-slate-800 rounded-xl p-8">
              <h2 className="text-2xl font-bold font-['Syne'] text-white mb-6 flex items-center">
                <Info size={24} className="mr-2 text-indigo-400" />
                About & Branding
              </h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Company Name</label>
                  <Input
                    value={content.about.company_name}
                    onChange={(e) => updateField('about', 'company_name', e.target.value)}
                    placeholder="DH Home Networking & Websites"
                    className="bg-slate-900 border-slate-800 text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Tagline</label>
                  <Input
                    value={content.about.tagline}
                    onChange={(e) => updateField('about', 'tagline', e.target.value)}
                    placeholder="Professional Web & Network Solutions"
                    className="bg-slate-900 border-slate-800 text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Company Description</label>
                  <Textarea
                    value={content.about.description}
                    onChange={(e) => updateField('about', 'description', e.target.value)}
                    placeholder="Expert website design and home networking services..."
                    rows={4}
                    className="bg-slate-900 border-slate-800 text-white resize-none"
                  />
                </div>

                {/* Team Members */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-slate-300">Team Members</label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addArrayItem('about', 'team', { name: '', role: '', bio: '', image_url: '' })}
                      className="border-slate-700 text-slate-300"
                    >
                      <Plus size={16} className="mr-1" /> Add Member
                    </Button>
                  </div>
                  {(content.about.team || []).map((member, idx) => (
                    <div key={idx} className="bg-slate-900 p-4 rounded-lg mb-3 border border-slate-800">
                      <div className="flex justify-between mb-2">
                        <span className="text-xs text-slate-500">Team Member {idx + 1}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeArrayItem('about', 'team', idx)}
                          className="text-red-400 hover:text-red-300 h-6 px-2"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <Input
                          value={member.name}
                          onChange={(e) => updateArrayItem('about', 'team', idx, { ...member, name: e.target.value })}
                          placeholder="Name"
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                        <Input
                          value={member.role}
                          onChange={(e) => updateArrayItem('about', 'team', idx, { ...member, role: e.target.value })}
                          placeholder="Role"
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                      <Textarea
                        value={member.bio}
                        onChange={(e) => updateArrayItem('about', 'team', idx, { ...member, bio: e.target.value })}
                        placeholder="Short bio"
                        rows={2}
                        className="bg-slate-800 border-slate-700 text-white resize-none"
                      />
                    </div>
                  ))}
                </div>
                
                <Button
                  onClick={() => saveSection('about')}
                  disabled={saving}
                  className="bg-indigo-500 hover:bg-indigo-600 text-white w-full"
                >
                  <Save size={18} className="mr-2" />
                  {saving ? 'Saving...' : 'Save About'}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* NAVIGATION/FOOTER EDITOR */}
          <TabsContent value="navigation" data-testid="navigation-editor">
            <div className="space-y-6">
              <div className="bg-slate-950 border border-slate-800 rounded-xl p-8">
                <h2 className="text-2xl font-bold font-['Syne'] text-white mb-6 flex items-center">
                  <Menu size={24} className="mr-2 text-indigo-400" />
                  Navigation & Footer
                </h2>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Logo Text</label>
                      <Input
                        value={content.navigation.logo_text}
                        onChange={(e) => updateField('navigation', 'logo_text', e.target.value)}
                        placeholder="DH Home Networking"
                        className="bg-slate-900 border-slate-800 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">CTA Button Text</label>
                      <Input
                        value={content.navigation.cta_text}
                        onChange={(e) => updateField('navigation', 'cta_text', e.target.value)}
                        placeholder="Client Portal"
                        className="bg-slate-900 border-slate-800 text-white"
                      />
                    </div>
                  </div>
                  
                  <Button
                    onClick={() => saveSection('navigation')}
                    disabled={saving}
                    className="bg-indigo-500 hover:bg-indigo-600 text-white w-full"
                  >
                    <Save size={18} className="mr-2" />
                    {saving ? 'Saving...' : 'Save Navigation'}
                  </Button>
                </div>
              </div>

              <div className="bg-slate-950 border border-slate-800 rounded-xl p-8">
                <h3 className="text-xl font-bold font-['Syne'] text-white mb-6">Footer Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Copyright Text</label>
                    <Input
                      value={content.footer.copyright}
                      onChange={(e) => updateField('footer', 'copyright', e.target.value)}
                      placeholder="DH Home Networking & Websites"
                      className="bg-slate-900 border-slate-800 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Footer Tagline</label>
                    <Input
                      value={content.footer.tagline}
                      onChange={(e) => updateField('footer', 'tagline', e.target.value)}
                      placeholder="Professional Web & Network Solutions"
                      className="bg-slate-900 border-slate-800 text-white"
                    />
                  </div>
                  
                  <Button
                    onClick={() => saveSection('footer')}
                    disabled={saving}
                    className="bg-indigo-500 hover:bg-indigo-600 text-white w-full"
                  >
                    <Save size={18} className="mr-2" />
                    {saving ? 'Saving...' : 'Save Footer'}
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-8 bg-slate-900/50 border border-slate-700 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
            <FileCode size={20} className="mr-2 text-indigo-400" />
            Editor Tips
          </h3>
          <ul className="text-sm text-slate-400 space-y-2">
            <li>• Changes are saved per section - click Save to persist</li>
            <li>• Public pages pull content from the database automatically</li>
            <li>• Use the Refresh button to reload latest content</li>
            <li>• For deployment to production, see the Deployment Guide</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminWebsiteEditor;
